import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import YouTube from 'react-youtube';
import UrlHelper from '../../utils/urlHelper';
import Loading from '../../components/shared/Loading';
import AppAndWebPreview from '../../components/app_preview/AppAndWebPreview';

import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  IconButton,
  LinearProgress,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ReplayIcon from '@mui/icons-material/Replay';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import SkipPreviousIcon from '@mui/icons-material/SkipPrevious';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';

const numberOrZero = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
};

const intervalDuration = (interval, exercise) => {
  const timeFromInterval = Math.abs(
    numberOrZero(interval?.endTime) - numberOrZero(interval?.startTime)
  );
  return timeFromInterval || numberOrZero(exercise?.duration);
};

const formatTime = (seconds) => {
  const safeSeconds = Math.max(0, Math.floor(numberOrZero(seconds)));
  const minutes = Math.floor(safeSeconds / 60);
  const remainingSeconds = safeSeconds % 60;

  return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
};

const exerciseKey = (exercise) =>
  [exercise?._id, exercise?.exerciseId, exercise?.title].filter(Boolean);

const buildExerciseMap = (workout) => {
  const exercises = [
    ...(workout?.exercises ?? []),
    ...(workout?.exerciseGroups ?? []).flatMap(
      (group) => group.exercises ?? []
    ),
  ];

  return exercises.reduce((map, exercise) => {
    exerciseKey(exercise).forEach((key) => {
      map[key] = exercise;
    });
    return map;
  }, {});
};

const getExerciseMedia = (exercise) => {
  const videoUrl = exercise?.videos?.find((video) => video?.url)?.url;
  const imageUrl =
    exercise?.images?.find((image) => image?.url)?.url ||
    exercise?.coverImages?.find((image) => image?.url)?.url ||
    exercise?.coverImageUrl;
  const youtubeVideoId =
    UrlHelper.extractYoutubeVideoIdFromYoutubeUrl(exercise?.youtubeVideoUrl) ||
    exercise?.youtubeVideoId;

  if (videoUrl && exercise?.workoutType !== 'YOUTUBE') {
    return { type: 'video', url: videoUrl, poster: imageUrl };
  }
  if (youtubeVideoId && exercise?.workoutType !== 'VIDEO') {
    return { type: 'youtube', videoId: youtubeVideoId };
  }
  if (imageUrl) {
    return { type: 'image', url: imageUrl };
  }
  return null;
};

const exercisePrescription = (exercise) => {
  if (!exercise) {
    return '';
  }

  const pieces = [];
  if (exercise.reps) {
    pieces.push(`${exercise.reps} reps`);
  }
  if (exercise.weight) {
    pieces.push(`${exercise.weight} ${exercise.weightUnit || 'lbs'}`);
  }
  if (exercise.percentOf1RM) {
    pieces.push(`${exercise.percentOf1RM}% 1RM`);
  }
  if (exercise.isTimed !== false && exercise.duration) {
    pieces.push(formatTime(exercise.duration));
  }
  return pieces.join(' • ');
};

const speechForInterval = (interval) => {
  const exercise = interval.exercise;
  if (interval.type === 'rest') {
    return 'Rest';
  }

  const reps = exercise?.reps ? `${exercise.reps} ` : '';
  const title = exercise?.title || 'Exercise';
  if (interval.isTimed && interval.duration > 0) {
    return `${reps}${title} for ${formatTime(interval.duration)}`;
  }
  return `${reps}${title}`;
};

const normalizeServerIntervals = (workout) => {
  const exerciseMap = buildExerciseMap(workout);

  return (workout?.intervals ?? [])
    .map((interval, index) => {
      const sourceExercise =
        exerciseMap[interval.exercise?._id] ??
        exerciseMap[interval.exercise?.exerciseId] ??
        exerciseMap[interval.exercise?.title];
      const mergedExercise = {
        ...(sourceExercise ?? {}),
        ...(interval.exercise ?? {}),
      };
      // Interval responses can omit media even when the exercise has it.
      ['images', 'videos'].forEach((field) => {
        if (!mergedExercise[field]?.some((media) => media?.url)) {
          mergedExercise[field] = sourceExercise?.[field];
        }
      });
      if (!mergedExercise.youtubeVideoId) {
        mergedExercise.youtubeVideoId = sourceExercise?.youtubeVideoId;
      }
      const type = (interval.intervalType ?? '').toLowerCase();
      const duration = intervalDuration(interval, mergedExercise);

      return {
        id: interval._id ?? `${type || 'interval'}-${index}`,
        type: type || 'exercise',
        round: interval.round,
        setNumber: interval.setNumber,
        exercise: mergedExercise,
        duration,
        isTimed: mergedExercise.isTimed !== false && duration > 0,
      };
    })
    .filter((interval) => interval.exercise?.title || interval.duration > 0);
};

const pushRestInterval = (intervals, seconds, label, key) => {
  if (numberOrZero(seconds) <= 0) {
    return;
  }

  intervals.push({
    id: key,
    type: 'rest',
    duration: numberOrZero(seconds),
    isTimed: true,
    exercise: {
      title: label,
      duration: numberOrZero(seconds),
      isTimed: true,
    },
  });
};

const normalizeFallbackIntervals = (workout) => {
  const groups =
    workout?.exerciseGroups?.length > 0
      ? workout.exerciseGroups
      : [
          {
            _id: 'workout',
            rounds: workout?.rounds || 1,
            restBetweenRounds: workout?.restBetweenRounds,
            exercises: workout?.exercises ?? [],
          },
        ];
  const intervals = [];

  groups.forEach((group, groupIndex) => {
    const rounds = Math.max(1, numberOrZero(group.rounds) || 1);
    for (let roundIndex = 0; roundIndex < rounds; roundIndex += 1) {
      (group.exercises ?? []).forEach((exercise, exerciseIndex) => {
        const sets = Math.max(1, numberOrZero(exercise.sets) || 1);
        for (let setIndex = 0; setIndex < sets; setIndex += 1) {
          const duration = numberOrZero(exercise.duration);
          intervals.push({
            id: [
              group._id ?? groupIndex,
              roundIndex,
              exercise._id ?? exercise.exerciseId ?? exerciseIndex,
              setIndex,
            ].join('-'),
            type: 'exercise',
            round: roundIndex + 1,
            setNumber: setIndex + 1,
            exercise,
            duration,
            isTimed: exercise.isTimed !== false && duration > 0,
          });

          if (setIndex < sets - 1) {
            pushRestInterval(
              intervals,
              exercise.restBetweenSetsDuration,
              'Rest between sets',
              `${exercise._id ?? exerciseIndex}-set-rest-${setIndex}`
            );
          }
        }

        pushRestInterval(
          intervals,
          exercise.restAfterExerciseDuration,
          'Rest',
          `${exercise._id ?? exerciseIndex}-exercise-rest-${roundIndex}`
        );
      });

      if (roundIndex < rounds - 1) {
        pushRestInterval(
          intervals,
          group.restBetweenRounds ?? workout?.restBetweenRounds,
          'Rest between rounds',
          `${group._id ?? groupIndex}-round-rest-${roundIndex}`
        );
      }
    }
  });

  return intervals;
};

const normalizeWorkoutIntervals = (workout) => {
  const serverIntervals = normalizeServerIntervals(workout);
  if (serverIntervals.length > 0) {
    return serverIntervals;
  }
  return normalizeFallbackIntervals(workout);
};

const WorkoutMedia = ({ exercise, workout, isPlaying }) => {
  const media = getExerciseMedia(exercise) || getExerciseMedia(workout);
  const videoRef = useRef(null);
  const [youtubePlayer, setYoutubePlayer] = useState(null);

  useEffect(() => {
    if (isPlaying == null) return;
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.play()?.catch(() => {});
      } else {
        videoRef.current.pause();
      }
    }
    if (youtubePlayer) {
      if (isPlaying) youtubePlayer.playVideo();
      else youtubePlayer.pauseVideo();
    }
  }, [isPlaying, media?.url, youtubePlayer]);

  if (media?.type === 'youtube') {
    return (
      <Box
        sx={{
          aspectRatio: '16 / 10',
          '& iframe': { width: '100%', height: '100%' },
          '& > div': { height: '100%' },
        }}
      >
        <YouTube
          key={media.videoId}
          videoId={media.videoId}
          opts={{
            width: '100%',
            height: '100%',
            playerVars: { playsinline: 1 },
          }}
          onReady={(event) => setYoutubePlayer(event.target)}
        />
      </Box>
    );
  }

  if (media?.type === 'video') {
    return (
      <Box
        component="video"
        key={media.url}
        ref={videoRef}
        aria-label={exercise?.title || workout?.title || 'Workout video'}
        src={media.url}
        poster={media.poster}
        preload="metadata"
        controls
        muted={isPlaying != null}
        playsInline
        sx={{
          width: '100%',
          aspectRatio: '16 / 10',
          objectFit: 'cover',
          backgroundColor: '#111827',
          borderRadius: 1,
        }}
      />
    );
  }

  if (media?.type === 'image') {
    return (
      <Box
        component="img"
        src={media.url}
        alt={exercise?.title || workout?.title || 'Workout'}
        sx={{
          width: '100%',
          aspectRatio: '16 / 10',
          objectFit: 'cover',
          backgroundColor: '#111827',
          borderRadius: 1,
        }}
      />
    );
  }

  return (
    <Box
      sx={{
        width: '100%',
        aspectRatio: '16 / 10',
        borderRadius: 1,
        backgroundColor: '#111827',
        color: '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <FitnessCenterIcon sx={{ fontSize: 56 }} />
    </Box>
  );
};

export default function WorkoutPreview({ workout }) {
  const intervals = useMemo(
    () => normalizeWorkoutIntervals(workout),
    [workout]
  );
  const totalDuration = useMemo(
    () => intervals.reduce((total, interval) => total + interval.duration, 0),
    [intervals]
  );
  const [currentIntervalIndex, setCurrentIntervalIndex] = useState(0);
  const [elapsedSecondsOnCurrentInterval, setElapsedSecondsOnCurrentInterval] =
    useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [completedIntervalIds, setCompletedIntervalIds] = useState([]);
  const [voiceEnabled, setVoiceEnabled] = useState(false);

  const currentInterval = intervals[currentIntervalIndex];
  const nextInterval = intervals[currentIntervalIndex + 1];
  const previousIntervalsDuration = intervals
    .slice(0, currentIntervalIndex)
    .reduce((total, interval) => total + interval.duration, 0);
  const elapsedTotal =
    previousIntervalsDuration + elapsedSecondsOnCurrentInterval;
  const remainingTotal = Math.max(0, totalDuration - elapsedTotal);
  const currentRemaining = currentInterval?.isTimed
    ? Math.max(0, currentInterval.duration - elapsedSecondsOnCurrentInterval)
    : null;
  const progress = totalDuration ? (elapsedTotal / totalDuration) * 100 : 0;
  const isComplete =
    intervals.length > 0 && currentIntervalIndex >= intervals.length;

  const speak = useCallback(
    (text) => {
      if (!voiceEnabled || !window.speechSynthesis || !text) {
        return;
      }
      window.speechSynthesis.cancel();
      window.speechSynthesis.speak(new SpeechSynthesisUtterance(text));
    },
    [voiceEnabled]
  );

  const completeCurrentInterval = useCallback(() => {
    if (!currentInterval) {
      return;
    }
    setCompletedIntervalIds((ids) =>
      ids.includes(currentInterval.id) ? ids : [...ids, currentInterval.id]
    );
  }, [currentInterval]);

  const goToInterval = useCallback(
    (index) => {
      const boundedIndex = Math.min(Math.max(index, 0), intervals.length);
      setCurrentIntervalIndex(boundedIndex);
      setElapsedSecondsOnCurrentInterval(0);
      if (boundedIndex >= intervals.length) {
        setIsPlaying(false);
        speak('Workout complete');
      } else {
        speak(speechForInterval(intervals[boundedIndex]));
      }
    },
    [intervals, speak]
  );

  const next = useCallback(() => {
    completeCurrentInterval();
    goToInterval(currentIntervalIndex + 1);
  }, [completeCurrentInterval, currentIntervalIndex, goToInterval]);

  const previous = useCallback(() => {
    goToInterval(currentIntervalIndex - 1);
  }, [currentIntervalIndex, goToInterval]);

  const restart = useCallback(() => {
    setCurrentIntervalIndex(0);
    setElapsedSecondsOnCurrentInterval(0);
    setCompletedIntervalIds([]);
    setIsPlaying(false);
    if (intervals[0]) {
      speak(speechForInterval(intervals[0]));
    }
  }, [intervals, speak]);

  const togglePlayback = useCallback(() => {
    if (!currentInterval && intervals.length > 0) {
      restart();
      return;
    }

    setIsPlaying((playing) => {
      const nextPlaying = !playing;
      if (nextPlaying && currentInterval) {
        speak(speechForInterval(currentInterval));
      } else if (!nextPlaying && window.speechSynthesis) {
        window.speechSynthesis.cancel();
      }
      return nextPlaying;
    });
  }, [currentInterval, intervals.length, restart, speak]);

  useEffect(() => {
    setCurrentIntervalIndex(0);
    setElapsedSecondsOnCurrentInterval(0);
    setCompletedIntervalIds([]);
    setIsPlaying(false);
  }, [workout?._id, intervals.length]);

  useEffect(() => {
    if (!isPlaying || !currentInterval?.isTimed) {
      return undefined;
    }

    const timer = window.setInterval(() => {
      setElapsedSecondsOnCurrentInterval((seconds) => {
        const nextSeconds = seconds + 1;
        if (nextSeconds >= currentInterval.duration) {
          window.clearInterval(timer);
          window.setTimeout(next, 0);
          return currentInterval.duration;
        }

        if (
          currentInterval.duration >= 20 &&
          currentInterval.duration - nextSeconds === 10
        ) {
          speak(
            currentInterval.type === 'rest' && nextInterval?.exercise?.title
              ? `${nextInterval.exercise.title} is next`
              : '10 seconds left'
          );
        }

        return nextSeconds;
      });
    }, 1000);

    return () => window.clearInterval(timer);
  }, [currentInterval, isPlaying, next, nextInterval, speak]);

  if (!workout) {
    return <Loading />;
  }

  if (
    intervals.length === 0 ||
    ['VIDEO', 'YOUTUBE'].includes(workout.workoutType)
  ) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Workout player preview
          </Typography>
          <Typography variant="h4" gutterBottom>
            {workout.title}
          </Typography>
          {getExerciseMedia(workout) ? (
            <WorkoutMedia workout={workout} />
          ) : (
            <Typography color="textSecondary">
              {['VIDEO', 'YOUTUBE'].includes(workout.workoutType)
                ? 'Add a workout video to preview playback.'
                : 'Add exercises to this workout to preview playback.'}
            </Typography>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <Stack spacing={3}>
      <Card>
        <CardContent>
          <Grid container spacing={3}>
            <Grid item xs={12} md={7}>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="overline" color="textSecondary">
                    Workout player preview
                  </Typography>
                  <Typography variant="h4">{workout.title}</Typography>
                </Box>

                <WorkoutMedia
                  key={currentInterval?.id || 'complete'}
                  exercise={currentInterval?.exercise}
                  workout={workout}
                  isPlaying={isPlaying}
                />

                <Stack direction="row" spacing={1} flexWrap="wrap">
                  <Chip
                    label={
                      currentInterval?.type === 'rest' ? 'Rest' : 'Exercise'
                    }
                    color={
                      currentInterval?.type === 'rest' ? 'secondary' : 'primary'
                    }
                  />
                  {currentInterval?.round && (
                    <Chip label={`Round ${currentInterval.round}`} />
                  )}
                  {currentInterval?.setNumber && (
                    <Chip label={`Set ${currentInterval.setNumber}`} />
                  )}
                  {currentInterval?.isTimed === false && (
                    <Chip label="Manual advance" />
                  )}
                </Stack>

                <Box>
                  <Typography variant="h5">
                    {isComplete
                      ? 'Workout complete'
                      : currentInterval?.exercise?.title || 'Exercise'}
                  </Typography>
                  {!isComplete && (
                    <Typography color="textSecondary">
                      {exercisePrescription(currentInterval?.exercise) ||
                        currentInterval?.exercise?.notes ||
                        'Complete this interval, then advance.'}
                    </Typography>
                  )}
                </Box>
              </Stack>
            </Grid>

            <Grid item xs={12} md={5}>
              <Stack spacing={3}>
                <Box
                  sx={{
                    p: 2,
                    border: '1px solid #dbe3ee',
                    borderRadius: 1,
                    backgroundColor: '#f8fafc',
                  }}
                >
                  <Stack spacing={1.5}>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography color="textSecondary">Current</Typography>
                      <Typography fontWeight={700}>
                        {currentRemaining == null
                          ? 'Manual'
                          : formatTime(currentRemaining)}
                      </Typography>
                    </Stack>
                    <LinearProgress
                      variant="determinate"
                      value={
                        currentInterval?.duration
                          ? (elapsedSecondsOnCurrentInterval /
                              currentInterval.duration) *
                            100
                          : completedIntervalIds.includes(currentInterval?.id)
                          ? 100
                          : 0
                      }
                    />
                    <Stack direction="row" justifyContent="space-between">
                      <Typography color="textSecondary">Elapsed</Typography>
                      <Typography>{formatTime(elapsedTotal)}</Typography>
                    </Stack>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography color="textSecondary">Remaining</Typography>
                      <Typography>{formatTime(remainingTotal)}</Typography>
                    </Stack>
                    <LinearProgress variant="determinate" value={progress} />
                  </Stack>
                </Box>

                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="center"
                  spacing={1}
                >
                  <IconButton
                    aria-label="Previous interval"
                    onClick={previous}
                    disabled={currentIntervalIndex === 0}
                  >
                    <SkipPreviousIcon />
                  </IconButton>
                  <IconButton
                    aria-label={isPlaying ? 'Pause' : 'Play'}
                    color="primary"
                    onClick={togglePlayback}
                    sx={{
                      width: 64,
                      height: 64,
                      backgroundColor: 'primary.main',
                      color: 'primary.contrastText',
                      '&:hover': { backgroundColor: 'primary.dark' },
                    }}
                  >
                    {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
                  </IconButton>
                  <IconButton
                    aria-label="Next interval"
                    onClick={next}
                    disabled={isComplete}
                  >
                    <SkipNextIcon />
                  </IconButton>
                  <IconButton aria-label="Restart workout" onClick={restart}>
                    <ReplayIcon />
                  </IconButton>
                </Stack>

                {currentInterval?.isTimed === false && !isComplete && (
                  <Button
                    variant="contained"
                    startIcon={<CheckCircleIcon />}
                    onClick={next}
                  >
                    Complete interval
                  </Button>
                )}

                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Stack direction="row" spacing={1} alignItems="center">
                    <VolumeUpIcon color="action" />
                    <Typography>Voice cues</Typography>
                  </Stack>
                  <Switch
                    checked={voiceEnabled}
                    onChange={(event) => setVoiceEnabled(event.target.checked)}
                  />
                </Stack>

                <Divider />

                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Up next
                  </Typography>
                  <Typography color="textSecondary">
                    {nextInterval?.exercise?.title || 'End of workout'}
                  </Typography>
                </Box>
              </Stack>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            App and web preview
          </Typography>
          <AppAndWebPreview
            mobilePreviewUrl={workout.mobilePreviewUrl}
            webUrl={workout.webUrl}
          />
        </CardContent>
      </Card>
    </Stack>
  );
}
